// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Pure geometry for the two-level icicle. Kept separate from rendering so it
// can be tested by POSITION — area-only assertions pass happily on a layout
// that stacks every block at the same origin, which is exactly how a broken
// treemap once shipped looking like garbage while conserving total area
// perfectly.

export interface IcicleInput {
  id: number;
  value: number;
  children: { id: number; value: number }[];
}

export interface IcicleRect {
  id: number;
  parent: number | null;
  x: number;
  y: number;
  w: number;
  h: number;
  depth: 0 | 1;
}

export interface IcicleOptions {
  width: number;
  rowHeight: number;
  gap: number;
  padTop?: number;
  padLeft?: number;
}

/**
 * Lay out top-level blocks proportionally to the widest one, then lay each
 * block's children proportionally WITHIN their parent's width.
 *
 * Children are sized against their parent's own width rather than the global
 * scale, which is what guarantees the containment property an icicle exists to
 * show: a child can never be drawn wider than, or outside, its parent.
 */
export function icicleLayout(groups: IcicleInput[], opts: IcicleOptions): IcicleRect[] {
  const { width, rowHeight, gap } = opts;
  const padTop = opts.padTop ?? 0;
  const padLeft = opts.padLeft ?? 0;
  const usable = width - padLeft * 2;

  const out: IcicleRect[] = [];
  const maxTop = Math.max(1, ...groups.map((g) => (Number.isFinite(g.value) ? g.value : 0)));

  let y = padTop;
  for (const g of groups) {
    const value = Number.isFinite(g.value) && g.value > 0 ? g.value : 0;
    const w = Math.max(0, (value / maxTop) * usable);
    out.push({ id: g.id, parent: null, x: padLeft, y, w, h: rowHeight, depth: 0 });

    const kids = g.children.filter((k) => Number.isFinite(k.value) && k.value > 0);
    const total = kids.reduce((a, k) => a + k.value, 0);
    const childY = y + rowHeight + gap;

    if (total > 0 && kids.length) {
      // The gaps come out of the parent's width, never added to it, so the row
      // of children ends exactly where the parent ends.
      const gaps = gap * Math.max(0, kids.length - 1);
      const avail = Math.max(0, w - gaps);
      let x = padLeft;
      kids.forEach((k, i) => {
        const kw = (k.value / total) * avail;
        out.push({ id: k.id, parent: g.id, x, y: childY, w: kw, h: rowHeight, depth: 1 });
        x += kw + (i < kids.length - 1 ? gap : 0);
      });
    }

    y = childY + rowHeight + gap * 2;
  }
  return out;
}

/** Total height the layout needs, so the caller's viewBox always contains it. */
export function icicleHeight(groups: IcicleInput[], opts: IcicleOptions): number {
  const padTop = opts.padTop ?? 0;
  return padTop + groups.length * (opts.rowHeight * 2 + opts.gap * 3);
}
